import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  useTransition,
  useDeferredValue,
} from "react";

import { Col, Flex, Row, Select, Spin, Typography } from "antd";
import { SyncOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { services } from "../../services";
import {
  dateTimeToUtc,
  getEndDateTime,
  getStartDateTime,
  utcDateTimeToLocal,
} from "../../shared";

import { servicesService } from "../../services/servicesService";
import SessionCalendar from "../../components/appointments/SessionCalendar";
import { MySelect } from "../../components/Forms";

const { Title, Text } = Typography;

export const ViewCalendarAppointment = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [serviceLookups, setServicesLookup] = useState([]);
  const [isPending, startTransition] = useTransition();

  const [filters, setFilters] = useState({
    serviceID: null,
  });

  const deferredServiceID = useDeferredValue(filters.serviceID);

  const currentRangeRef = useRef({
    startDate: dayjs().startOf("month").format("YYYY-MM-DD"),
    endDate: dayjs().endOf("month").format("YYYY-MM-DD"),
  });

  // const serviceOptions = useMemo(() => {
  //   return serviceLookups?.map((list) => ({
  //     value: list?.id,
  //     label: list?.name,
  //   }));
  // }, [serviceLookups]);

  const getSessions = useCallback(
    async (range) => {
      try {
        setLoading(true);

        if (range?.startDate && range?.endDate) {
          currentRangeRef.current = {
            startDate: dayjs(range.startDate).format("YYYY-MM-DD"),
            endDate: dayjs(range.endDate).format("YYYY-MM-DD"),
          };
        }

        const { startDate, endDate } = currentRangeRef.current;

        const params = {
          startDate: dateTimeToUtc(getStartDateTime(startDate)),
          endDate: dateTimeToUtc(getEndDateTime(endDate)),
          serviceID: deferredServiceID,
          pagination: false,
        };

        const response = await services?.sessionsService?.getSessions(params);

        if (!Array.isArray(response)) {
          setSessions([]);
          return;
        }

        // 🔥 Browser ko render ka chance deta hai
        await new Promise((resolve) => requestAnimationFrame(resolve));

        const formattedEvents = new Array(response.length);

        for (let i = 0; i < response.length; i++) {
          const session = response[i];

          if (!session) continue;

          const appointment = session.appointment;
          const clientInfo = appointment?.client_info;
          const medicalInfo = clientInfo?.medical_info;

          const localDate = utcDateTimeToLocal(session.sessionDateTime);

          const sessionStart = dayjs(localDate).toDate();

          const issues = [];

          if (medicalInfo) {
            const diabetes = medicalInfo?.isDiabetes;
            const hiv = medicalInfo?.HIV;
            const asthma = medicalInfo?.isAsthma;
            const hepatitis = medicalInfo?.hepatitis;
            const bp = medicalInfo?.BP;

            if (diabetes === "y" || diabetes === "yes") issues.push("Diabetes");

            if (hiv === "y" || hiv === "yes") issues.push("HIV");

            if (asthma === "y" || asthma === "yes") issues.push("Asthma");

            if (hepatitis === "y" || hepatitis === "yes")
              issues.push("Hepatitis");

            if (bp === "y" || bp === "yes") issues.push("BP");
          }

          const serviceName = session?.service?.name || "";

          formattedEvents[i] = {
            id: session?.id,
            alldata: session,

            patientName: clientInfo?.name || "",
            patientFather: clientInfo?.fatherName || "",
            patientAge: clientInfo?.age || "",
            contact: clientInfo?.telephone || "",
            countryCode: clientInfo?.countryCode || "",
            cnic: clientInfo?.cnic_passport || "",
            address: clientInfo?.address || "",
            occuption: clientInfo?.occuption || "",
            s_o_info: clientInfo?.other_source || "",

            issues,

            start: sessionStart,
            end: new Date(sessionStart.getTime() + 60 * 60 * 1000),

            title: clientInfo?.name || "",

            status: session?.status?.toLowerCase?.() || "pending",

            displayAmount: serviceName.toLowerCase().includes("prp")
              ? appointment?.duePayment
              : appointment?.advancePayment,

            payment: {
              status: appointment?.advancePayment > 0 ? "Paid" : "Pending",

              amount: Number(session?.price),

              totalamount: Number(appointment?.totalPayment),

              advanceamount: Number(appointment?.advancePayment),

              balanceamount: Number(appointment?.duePayment),
            },
          };
        }

        startTransition(() => {
          setSessions(formattedEvents);
        });
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    },
    [deferredServiceID],
  );

  useEffect(() => {
    getSessions();
  }, [deferredServiceID]);

  const handleServiceChange = (value) => {
    startTransition(() => {
      setFilters((prev) => ({
        ...prev,
        serviceID: value ?? null,
      }));
    });
  };

  useEffect(() => {
    getServicesLookUp();
  }, []);

  const getServicesLookUp = async () => {
    try {
      const result = await servicesService?.getServicesLookUp();

      setServicesLookup(result || []);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Flex vertical gap={16}>
      <Row gutter={[16, 16]} justify="space-between">
        <Col span={24} lg={12}>
          <Flex vertical gap={1}>
            <Title level={4} className="m-0">
              Sessions
            </Title>

            <Text className="fs-13">{sessions?.length} sessions</Text>
          </Flex>
        </Col>

        <Col xs={24} sm={8} lg={4}>
          {/* <div style={{ marginBottom: 8 }}>
            <label style={{ fontWeight: 500 }}>Service Type</label>
          </div>
          <Select
            style={{ width: "100%" }}
            placeholder="All"
            value={filters.serviceID}
            onChange={handleServiceChange}
            options={serviceOptions}
            loading={isPending}
            filterOption={(input, option) =>
              option?.children?.toLowerCase().includes(input.toLowerCase())
            }
            showSearch
            allowClear
          /> */}
          <MySelect
            withoutForm
            label="Services"
            allowClear
            placeholder="Select services..."
            value={filters.serviceID}
            onChange={handleServiceChange}
            options={serviceLookups}
            style={{ width: "100%" }}
            filterOption={(input, option) => {
              const labelText = String(option?.children ?? "").toLowerCase();
              const cleanedInput = input
                .toLowerCase()
                .replace(/\s+/g, " ")
                .trimEnd();

              return labelText.includes(cleanedInput);
            }}
            showSearch
          />
        </Col>
      </Row>

      <div style={{ position: "relative" }}>
        {(loading || isPending) && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 20,
              background: "rgba(255,255,255,0.4)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Spin
              indicator={
                <SyncOutlined
                  spin
                  style={{
                    color: "var(--main-color)",
                    fontSize: 24,
                  }}
                />
              }
            />
          </div>
        )}

        <SessionCalendar
          sessions={sessions}
          getSessions={getSessions}
          filter={filters}
          setFilters={setFilters}
        />
      </div>
    </Flex>
  );
};

export default ViewCalendarAppointment;
